import React, { useState } from 'react';
import { Alert, Button, Card, Form, Input, Radio, Space, Typography } from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { useAuth } from '../../AuthProvider';
import AddCompanyService from '../../services/AddCompanyService';
import AddBrandImage from '../../services/AddBrandImage';

const { Title, Text } = Typography;
const MAX_FILE_SIZE = 10 * 1024 * 1024;

const createLocationId = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return `loc-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const AddCompany = () => {
    const authContext = useAuth();
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [saved, setSaved] = useState(false);
    const [createdCompany, setCreatedCompany] = useState(null);
    const [imageData, setImageData] = useState({});

    const pickImage = (event) => {
        const file = event?.target?.files?.[0];
        if (!file) return;
        if (file.size > MAX_FILE_SIZE) {
            setError('The file size is too large. Max 10 MB.');
            return;
        }
        setError('');
        const localUrl = URL.createObjectURL(file);
        setImageData({ file, localUrl, fileName: file.name });
    };

    const removeImage = () => {
        setImageData({});
    };

    const selectDefaultLocation = (selectedIndex) => {
        const currentLocations = Array.isArray(form.getFieldValue('locations')) ? form.getFieldValue('locations') : [];
        const updated = currentLocations.map((loc, index) => ({
            ...(loc || {}),
            defaultLocation: index === selectedIndex ? 'Y' : 'N',
        }));
        form.setFieldsValue({ locations: updated });
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            setSaving(true);
            setError('');
            setSaved(false);
            setCreatedCompany(null);

            const locations = (Array.isArray(values.locations) ? values.locations : [])
                .map((loc) => ({
                    id: loc.id || createLocationId(),
                    streetNo: (loc.streetNo || '').trim(),
                    streetName: (loc.streetName || '').trim(),
                    city: (loc.city || '').trim(),
                    country: (loc.country || '').trim(),
                    suburb: (loc.suburb || '').trim(),
                    defaultLocation: loc.defaultLocation === 'Y' ? 'Y' : 'N',
                }))
                .filter((loc) => loc.streetNo || loc.streetName || loc.city || loc.country || loc.suburb);

            const payload = {
                companyName: values.companyName,
                companyABN: values.companyABN,
                remail: values.remail,
                rfName: values.rfName,
                rlName: values.rlName,
                locations,
                brandImageUrl: '',
            };

            if (imageData?.file) {
                const uploadForm = new FormData();
                uploadForm.append('file', imageData.file);
                const brandUpload = await AddBrandImage(uploadForm, authContext.token);
                const brandImageUrl = brandUpload?.brandImageUrl || brandUpload?.data?.brandImageUrl || '';
                const brandImageKeyName = brandUpload?.brandImageKeyName || brandUpload?.data?.brandImageKeyName || '';
                if (brandImageUrl) {
                    payload.brandImageUrl = brandImageUrl;
                    payload.brandImageKeyName = brandImageKeyName;
                }
            }

            const result = await AddCompanyService(payload, authContext.token);
            setCreatedCompany(result);
            setSaved(true);
            form.resetFields();
            setImageData({});
        } catch (err) {
            if (err?.errorFields) return;
            console.error(err);
            setError('Failed to create company. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card>
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <div>
                    <Title level={4} style={{ margin: 0 }}>Add Company</Title>
                    <Text type="secondary">Create a new company and invite the root manager.</Text>
                </div>

                {error ? <Alert type="error" showIcon message={error} /> : null}
                {saved && createdCompany ? (
                    <Alert
                        type="success"
                        showIcon
                        message={`Company created — ${createdCompany.companyName || ''} (ID: ${createdCompany.companyId || createdCompany.id || '—'}) · Manager: ${createdCompany.email || ''}`}
                    />
                ) : null}

                <Form form={form} layout="vertical" initialValues={{ locations: [{ streetNo: '', streetName: '', city: '', country: '' }] }}>
                    <Form.Item name="companyName" label="Company Name" rules={[{ required: true, message: 'Company name is required' }]}>
                        <Input placeholder="Acme Pty Ltd" />
                    </Form.Item>
                    <Form.Item
                        name="companyABN"
                        label="Company ABN"
                        rules={[
                            { required: true, message: 'ABN is required' },
                            { pattern: /^\d{11}$/, message: 'ABN must be 11 digits' },
                        ]}
                    >
                        <Input placeholder="12345678901" />
                    </Form.Item>
                    <Form.Item
                        name="remail"
                        label="Root Manager Email"
                        rules={[
                            { required: true, message: 'Manager email is required' },
                            { type: 'remail', message: 'Enter a valid email' },
                        ]}
                    >
                        <Input placeholder="manager@company.com" />
                    </Form.Item>
                    <Form.Item name="rfName" label="Root Manager First Name" rules={[{ required: true, message: 'First name is required' }]}>
                        <Input placeholder="Jane" />
                    </Form.Item>
                    <Form.Item name="rlName" label="Root Manager Last Name" rules={[{ required: true, message: 'Last name is required' }]}>
                        <Input placeholder="Doe" />
                    </Form.Item>

                    <Form.List name="locations">
                        {(fields, { add, remove }) => (
                            <div>
                                <Text strong>Company Locations</Text>
                                {fields.map((field, index) => (
                                    <Card key={field.key} size="small" style={{ marginTop: 12 }}>
                                        <div style={{ marginBottom: 8 }}>
                                            <Radio
                                                checked={form.getFieldValue(['locations', field.name, 'defaultLocation']) === 'Y'}
                                                onChange={() => selectDefaultLocation(field.name)}
                                            >
                                                Mark as default location
                                            </Radio>
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
                                            <Form.Item name={[field.name, 'id']} hidden>
                                                <Input />
                                            </Form.Item>
                                            <Form.Item name={[field.name, 'defaultLocation']} hidden>
                                                <Input />
                                            </Form.Item>
                                            <Form.Item name={[field.name, 'streetNo']} label={`Street No ${index + 1}`}>
                                                <Input placeholder="99" />
                                            </Form.Item>
                                            <Form.Item name={[field.name, 'streetName']} label="Street Name">
                                                <Input placeholder="Main Street" />
                                            </Form.Item>
                                            <Form.Item name={[field.name, 'suburb']} label="Suburb">
                                                <Input placeholder="Suburb" />
                                            </Form.Item>
                                            <Form.Item name={[field.name, 'city']} label="City">
                                                <Input placeholder="Sydney" />
                                            </Form.Item>
                                            <Form.Item name={[field.name, 'country']} label="Country">
                                                <Input placeholder="Australia" />
                                            </Form.Item>
                                        </div>
                                        {fields.length > 1 ? (
                                            <Button
                                                type="link"
                                                danger
                                                icon={<MinusCircleOutlined />}
                                                onClick={() => remove(field.name)}
                                                style={{ padding: 0 }}
                                            >
                                                Remove location
                                            </Button>
                                        ) : null}
                                    </Card>
                                ))}
                                <Button
                                    type="dashed"
                                    onClick={() => add({ id: createLocationId(), defaultLocation: 'N', streetNo: '', streetName: '', suburb: '', city: '', country: '' })}
                                    icon={<PlusOutlined />}
                                    style={{ marginTop: 12 }}
                                >
                                    Add location
                                </Button>
                            </div>
                        )}
                    </Form.List>

                    <div style={{ marginTop: 16 }}>
                        <Text strong>Brand Image</Text>
                        {imageData.localUrl ? (
                            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 8 }}>
                                <div style={{ position: 'relative' }}>
                                    <img
                                        src={imageData.localUrl}
                                        alt="Brand"
                                        style={{ width: 200, height: 200, borderRadius: 10, objectFit: 'cover' }}
                                    />
                                    <button
                                        type="button"
                                        onClick={removeImage}
                                        style={{
                                            position: 'absolute', top: -10, right: -10,
                                            backgroundColor: '#d32f2f', color: '#fff',
                                            border: 'none', borderRadius: '50%',
                                            width: 30, height: 30, cursor: 'pointer',
                                            fontWeight: 'bold', fontSize: 18,
                                            lineHeight: '30px', textAlign: 'center',
                                        }}
                                    >×</button>
                                </div>
                            </div>
                        ) : null}
                        <label style={{ display: 'block', marginTop: 8 }}>
                            <input type="file" accept="image/*" onChange={pickImage} style={{ display: 'none' }} />
                            <Button block>{imageData.file ? 'Change image' : 'Select Brand Image'}</Button>
                        </label>
                        {imageData.fileName ? <Text type="secondary" style={{ marginTop: 4, display: 'block' }}>{imageData.fileName}</Text> : null}
                    </div>
                </Form>

                <Button type="primary" loading={saving} onClick={handleSave}>
                    Create Company &amp; Invite Manager
                </Button>
            </Space>
        </Card>
    );
};

export default AddCompany;
